import React from 'react';

import type { IdAccountDto } from '@/services/ws-crm/src/main/resources/rest-api';
import { getAccountList } from '@/services/ws-crm/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY } from 'src/constants';
import type { Option } from 'src/types';

type UseAccountsOptionsReturn = {
  options: Option<IdAccountDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

type UseAccountsOptions = (includeInactive: boolean) => UseAccountsOptionsReturn;

export const useAccountsOptions: UseAccountsOptions = (includeInactive) => {
  const [searchFilter, setSearchFilter] = React.useState<string>('');

  const getAccountListRequest = useRequest({
    apiMethod: getAccountList,
    params: { prefix: searchFilter, isActive: includeInactive },
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getAccountListRequest.data$,
    (response) =>
      response?.data.items.map((item) => ({
        label: item?.displayName ?? '',
        value: item.id,
        active: item.active,
      })) ?? [],
  );
  const total = useSubscribe(getAccountListRequest.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(getAccountListRequest.pending$);

  return React.useMemo<UseAccountsOptionsReturn>(
    () => ({
      options,
      pending,
      total,
      searchFilter,
      setSearchFilter,
    }),
    [options, pending, total, searchFilter],
  );
};
